import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import SimulationResultsContent from './SimulationResultsContent';

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Public view wrapper for shared simulations
 * No authentication required - uses share token instead of simulation ID
 */
const PublicSimulationView: React.FC = () => {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [simulationName, setSimulationName] = useState<string>('');
  const [isLoadingName, setIsLoadingName] = useState(true);

  const handleError = (error: string) => {
    console.error('Public simulation error:', error);
  };

  useEffect(() => {
    const fetchSimulationName = async () => {
      if (!shareToken) return;

      setIsLoadingName(true);
      try {
        const response = await fetch(`${API_URL}/simulations/public/${shareToken}`, {
          credentials: 'omit',
        });

        if (response.ok) {
          const data = await response.json();
          if (data.name) {
            setSimulationName(data.name);
          } else {
            setSimulationName('Shared Simulation');
          }
        } else {
          setSimulationName('Shared Simulation');
        }
      } catch (err) {
        console.error('Error fetching simulation name:', err);
        setSimulationName('Shared Simulation');
      } finally {
        setIsLoadingName(false);
      }
    };

    fetchSimulationName();
  }, [shareToken]);

  if (!shareToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Invalid Share Link</h2>
          <p className="text-gray-600">
            This simulation link is not valid. Please check the URL and try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Simple header for public view */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <a
              href={import.meta.env.VITE_BLUEPILL_LANDING_PAGE || 'https://blue-pill.ai'}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
            >
              <img
                src={`${import.meta.env.VITE_PUBLIC_URL}/logo.png`}
                alt="BluePill AI"
                className="h-16"
              />
              <h1 className="text-xl font-semibold text-gray-900">
                {isLoadingName ? 'Loading...' : simulationName}
              </h1>
            </a>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <SimulationResultsContent
          simulationId={shareToken}
          isPublicView={true}
          onError={handleError}
          defaultChatOpen={false}
        />
      </div>

      {/* Footer with branding */}
      <div className="py-6 text-center bg-gray-50 border-t border-gray-200">
        <p className="text-sm text-gray-500">
          Powered by{' '}
          <a
            href={import.meta.env.VITE_BLUEPILL_LANDING_PAGE || 'https://blue-pill.ai'}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:text-primary-hover"
          >
            BluePill AI
          </a>
        </p>
      </div>
    </div>
  );
};

export default PublicSimulationView;
